<script setup>
/**
 * 「我的团队」页，逐条对齐旧版 web/12.0195d2b4b240a93b0284.js 里的
 * app-team 组件（类 s、模板工厂 D + 子模板 q/R/S、样式 M）。
 * 旧版路由是 /tabs/recommend/team。
 *
 * 旧版类 s：
 *   ngOnInit → getRecommendLists()
 *   getRecommendLists(l): POST /game/recommend/lists {page}
 *     注意这里取的是 u.data 本身（不是 data.lists）：
 *       lists = page_index === 1 ? u.data : lists.concat(u.data)
 *   doRefresh(l): page_index = 1; getRecommendLists(l)
 *   doInfinite(l): page_index += 1；超过 page_count 就 toast「已经最后一页了」
 *
 * 模板：
 *   ion-header：ion-button[routerLink="/tabs/recommend"] + ion-title「我的团队」
 *   ion-content（没有 padding）
 *     *ngIf="lists" → ion-list.list-ios
 *         ion-list-header「 我的团队 」
 *         ngFor lists → ion-item > ion-label.list-item
 *             span.index（序号，index+1）/ span.username / span.nickname
 *     ion-refresher + ion-infinite-scroll
 *
 * 样式 M：
 *   .list-item{display:flex;align-items:center}
 *   .list-item span{flex-grow:1;text-align:center}
 *   .list-item span:first-child{text-align:left}
 */
import { computed, onMounted, ref } from 'vue'
import { request } from '../services/api'
import PullRefresher from '../components/PullRefresher.vue'
import AppBottomNav from '../components/AppBottomNav.vue'

const lists = ref(null)
const page = ref({ item_total: 0, page_index: 1, page_size: 15, page_count: 0 })
const loading = ref(false)
const notice = ref('')
let noticeTimer = null

/** 旧版这些提示走的是 ion-toast（底部浮层、自动消失），不在页面里占位置 */
function showToast(message) {
  notice.value = message
  if (noticeTimer) window.clearTimeout(noticeTimer)
  noticeTimer = window.setTimeout(() => { notice.value = '' }, 2000)
}

const hasMore = computed(() => page.value.page_count > 1 && page.value.page_count != page.value.page_index)

/** 旧版 getRecommendLists(l=null) */
async function getRecommendLists(event = null) {
  loading.value = true
  try {
    const response = await request('/game/recommend/lists', {
      method: 'POST',
      data: { page: page.value.page_index },
    })
    page.value = { ...page.value, ...(response.page || {}) }
    // 旧版这里用的是 response.data 本身，不是 data.lists
    const incoming = Array.isArray(response.data) ? response.data : []
    lists.value = page.value.page_index === 1 ? incoming : [...(lists.value || []), ...incoming]
  } catch (error) {
    showToast(error.message || '加载失败')
  } finally {
    loading.value = false
    event?.complete()
  }
}

/** 旧版 doRefresh(l) */
function doRefresh(event) {
  page.value = { ...page.value, page_index: 1 }
  void getRecommendLists(event)
}

/** 旧版 doInfinite(l) */
function doInfinite() {
  const next = page.value.page_index + 1
  if (next > page.value.page_count) { showToast('已经最后一页了'); return }
  page.value = { ...page.value, page_index: next }
  void getRecommendLists()
}

onMounted(() => { void getRecommendLists() })
</script>

<template>
  <div class="team-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮 routerLink="/tabs/recommend" -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/recommend" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">我的团队</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <PullRefresher
      pulling-text="下拉刷新"
      refreshing-text="下拉刷新..."
      refreshing-spinner="circles"
      @refresh="doRefresh"
    >
      <!-- 旧版 q：*ngIf="lists"。模板里写死了 class="list-ios"，md 模式下这个类没有对应
           样式，属于旧版模板残留，这里保留类名不赋样式 -->
      <section v-if="lists" class="ion-list list-ios">
        <div class="list-header"> 我的团队 </div>
        <div v-for="(row, index) in lists" :key="row.member_id ?? index" class="list-item">
          <div class="list-item-inner">
            <!-- 旧版这里 .list-item 是挂在 ion-label 上的 -->
            <div class="ion-label list-item-label">
              <span class="index"> {{ index + 1 }} </span>
              <span class="username"> {{ row.member_username }} </span>
              <span class="nickname"> {{ row.member_nickname }} </span>
            </div>
          </div>
        </div>
      </section>

      <!-- 旧版 S：ion-infinite-scroll，loadingSpinner="bubbles" loadingText="正在加载" -->
      <button v-if="hasMore" class="load-more" type="button" :disabled="loading" @click="doInfinite">
        正在加载
      </button>
    </PullRefresher>

    <!-- 旧版 appService.showToast()：底部浮层 -->
    <div v-if="notice" class="ion-toast">
      <div class="toast-wrapper"><div class="toast-message">{{ notice }}</div></div>
    </div>

    <!-- 旧版这一页的路由是 /tabs/recommend/team，属于 tabs 的子路由，
         ion-tabs 的 tab 栏在子页面里一直挂着，所以这里也带底部导航 -->
    <AppBottomNav />
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
/* padding-bottom 是给底部 tab 栏（fixed，56px）留位 */
.team-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif;padding-bottom:56px;box-sizing:border-box}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-header{display:flex;align-items:center;justify-content:space-between;width:100%;margin:0;padding:0 16px;min-height:45px;font-size:14px;background:transparent;color:#000;overflow:hidden}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}

/* --- 以下逐条照抄旧版样式表 M（chunk 12 的 app-team）。
       旧版类名就叫 .list-item，为了不和上面 ion-item 的 .list-item 撞，
       这里挂在 ion-label 上的那个改名成 .list-item-label，规则内容照抄 --- */
.list-item-label{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr));align-items:center;width:100%;white-space:normal;text-overflow:clip;overflow:visible}
.list-item-label span{min-width:0;overflow:hidden;text-overflow:ellipsis;text-align:center}
.list-item-label span:first-child{text-align:left}

/* ion-toast（md）的形态还原，具体数值未从旧版产物逐条核对 */
.ion-toast{position:absolute;left:0;right:0;bottom:0;display:flex;justify-content:center;pointer-events:none;z-index:100000}
.toast-wrapper{
  margin:0 16px 16px;max-width:700px;
  border-radius:4px;padding:14px 16px;
  background:#333;color:#fff;font-size:14px;line-height:20px;
  box-shadow:0 3px 5px -1px rgb(0 0 0 / 20%),0 6px 10px 0 rgb(0 0 0 / 14%),0 1px 18px 0 rgb(0 0 0 / 12%);
}

.load-more{display:block;width:calc(100% - 32px);margin:10px 16px;padding:8px 0;border:1px solid #ddd;border-radius:4px;background:#fafafa;font-size:.8rem}
</style>
